// ── Assistant Context (ClientApp/src/AssistantContext.tsx) ──
// One conversation shared by every assistant surface. The Header robot icon, the per-page ask box,
// and the chat modal all read and drive the same message list here, so typing into the box, then
// continuing in the modal, is one continuous thread rather than three disconnected chats.
//
// Retrieval is grounded on the current message server-side; history is advisory phrasing context
// only. The whole surface is gated on `Assistant.Guidance` at the render sites — this provider is
// inert (holds empty state) when the flag is off, so it is safe to mount unconditionally.

import React from 'react';
import {
  sendAssistantChat,
  sendAssistantFeedback,
  sendAssistantCollectTurn,
  type AssistantSource,
  type AssistantDraftItem,
  type AssistantCollectResponse,
} from './api/assistant';
import { createRequest } from './api/requests';
import { newEvidenceSubmission, submitWithEvidence } from './api/bugEvidence';
import type { AssistantCollectField } from './api/assistant';

/** An inline collection card carried on an assistant turn — a review-and-submit or a form hand-off. */
export interface CollectCard {
  attachments?: import('./api/requests').AttachmentsConfig;
  reviewFields?: AssistantCollectField[];
  status: 'review' | 'handoff';
  requestType: string;
  displayName: string;
  draft?: AssistantDraftItem[];
  titlePreview?: string;
  formUrl?: string;
  /** Absolute external URL to open, when a router option forwards outside AskIAM. */
  externalUrl?: string;
  collected: Record<string, string>;
}

/** A rendered chat message. Assistant turns carry the server-authoritative enrichments. */
export interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: AssistantSource[];
  intent?: string;
  fallback?: string | null;
  helpdeskUrl?: string | null;
  degraded?: boolean;
  /** Correlation id of the underlying review, needed to vote on this answer. */
  reviewId?: string;
  /** The user's thumbs up/down on this answer, once cast (drives the button state). */
  feedback?: 'up' | 'down';
  /** True when this assistant turn is a client-side error notice, so it is excluded from history. */
  error?: boolean;
  /** Present when this assistant turn is a guided-collection review/hand-off card. */
  collect?: CollectCard;
  /** True for any turn that belongs to a guided collection, so it is excluded from guidance chat history. */
  collectTurn?: boolean;
}

/** Where a collection conversation currently stands; drives whether the input feeds the collector. */
export type CollectStatus = 'idle' | 'collecting' | 'review' | 'handoff';

interface AssistantContextValue {
  messages: AssistantMessage[];
  /** True while a turn is in flight. */
  loading: boolean;
  /** Whether the chat modal is open. */
  open: boolean;
  /** Where any active guided-collection conversation stands. */
  collectStatus: CollectStatus;
  openAssistant: () => void;
  closeAssistant: () => void;
  /** Clears the thread and starts a new conversation id. */
  resetConversation: () => void;
  /** Appends the user turn, calls the endpoint, and appends the reply (or an error notice). */
  sendMessage: (text: string) => Promise<void>;
  /** Records a thumbs up/down on the assistant answer at the given index (best-effort). */
  sendFeedback: (messageIndex: number, helpful: boolean) => void;
  /** Begins a guided request-collection conversation for a request type. */
  startCollect: (requestType: string, displayName: string) => Promise<void>;
  /** Feeds the user's answer to the collector and advances one turn. */
  submitCollectAnswer: (text: string) => Promise<void>;
  /** Confirms and submits the assembled review draft; returns the created request id, or null on error. */
  confirmCollectSubmit: () => Promise<string | null>;
  /** Revalidates edited review values and replaces the review card with the canonical server response. */
  refreshCollectReview: (values: Record<string, string>) => Promise<void>;
  /** Abandons the current collection conversation. */
  cancelCollect: () => void;
  evidenceFiles: File[];
  setEvidenceFiles: (files: File[]) => void;
  reviewValues: Record<string, string>;
  setReviewValues: (values: Record<string, string>) => void;
  savedRequestId?: string;
}

const AssistantContext = React.createContext<AssistantContextValue | undefined>(undefined);

function newConversationId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `conv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

interface ActiveCollection {
  requestType: string;
  displayName: string;
  collected: Record<string, string>;
  skippedFields: string[];
  reviewFields?: AssistantCollectField[];
}

function mergeReviewValues(
  active: ActiveCollection,
  values: Record<string, string>,
): { payload: Record<string, string>; skippedFields: string[] } {
  const payload = { ...active.collected };
  const skippedFields = new Set(active.skippedFields);
  for (const field of active.reviewFields ?? []) {
    if (!(field.key in values)) continue;
    const value = values[field.key].trim();
    if (value) {
      payload[field.key] = value;
      skippedFields.delete(field.key);
    } else {
      delete payload[field.key];
      if (!field.required) skippedFields.add(field.key);
    }
  }
  return { payload, skippedFields: [...skippedFields] };
}

export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = React.useState<AssistantMessage[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [evidenceFiles, setEvidenceFiles] = React.useState<File[]>([]);
  const [reviewValues, setReviewValues] = React.useState<Record<string, string>>({});
  const evidenceSubmission = React.useRef(newEvidenceSubmission());

  const conversationIdRef = React.useRef<string>(newConversationId());
  // Mirror of `messages` so `sendMessage` builds history from the latest turns without depending on
  // (and being recreated by) every message change.
  const messagesRef = React.useRef<AssistantMessage[]>([]);
  messagesRef.current = messages;
  // Guards `sendMessage` against re-entry. Written imperatively inside `sendMessage` rather than
  // mirrored from `loading` during render: `setLoading(true)` only schedules a state update, so a
  // render-phase mirror stays false until React commits and two rapid submits both pass the guard.
  // Matches the `busyRef` pattern in DeflectionCatalogPage.
  const loadingRef = React.useRef(false);

  const openAssistant = React.useCallback(() => setOpen(true), []);
  const closeAssistant = React.useCallback(() => setOpen(false), []);

  const resetConversation = React.useCallback(() => {
    if (loadingRef.current) return;
    evidenceSubmission.current = newEvidenceSubmission();
    setEvidenceFiles([]); setReviewValues({});
    setMessages([]);
    conversationIdRef.current = newConversationId();
    // A new conversation abandons any in-flight guided collection so the input stops feeding a
    // collector whose messages were just cleared.
    collectRef.current = null;
    setCollectStatus('idle');
  }, []);

  const sendMessage = React.useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loadingRef.current) return;
    loadingRef.current = true;

    const history = messagesRef.current
      .filter((m) => !m.error && !m.collectTurn)
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setLoading(true);
    try {
      const res = await sendAssistantChat({
        message: trimmed,
        history,
        conversationId: conversationIdRef.current,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.reply,
          sources: res.sources,
          intent: res.intent,
          fallback: res.fallback,
          helpdeskUrl: res.helpdeskUrl,
          degraded: res.degraded,
          reviewId: res.reviewId,
        },
      ]);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'The assistant is unavailable right now. Please try again in a moment.';
      setMessages((prev) => [...prev, { role: 'assistant', content: message, error: true }]);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  const sendFeedback = React.useCallback((messageIndex: number, helpful: boolean) => {
    const target = messagesRef.current[messageIndex];
    if (!target || target.role !== 'assistant' || target.error) return;

    const next: 'up' | 'down' = helpful ? 'up' : 'down';
    // Optimistically reflect the vote; it is a best-effort side channel, so a failed POST just
    // leaves the button un-pressed rather than surfacing an error into the conversation.
    setMessages((prev) =>
      prev.map((m, i) => (i === messageIndex ? { ...m, feedback: next } : m)),
    );

    if (!target.reviewId) return; // capability/greeting turns have no review to attribute a vote to.
    const targetValue = target.sources && target.sources.length > 0 ? target.sources[0].targetValue : undefined;
    void sendAssistantFeedback({ reviewId: target.reviewId, helpful, targetValue }).catch(() => {
      // Swallow — feedback must never disrupt the chat. The optimistic state stands.
    });
  }, []);

  // ── Guided request collection ──
  // The server is authoritative: every turn returns the canonical `collected` map, which replaces the
  // local copy wholesale. `collectRef` holds only advisory state between turns; nothing here is trusted
  // at submit time (POST /api/requests re-validates the whole payload).
  const [collectStatus, setCollectStatus] = React.useState<CollectStatus>('idle');
  const collectRef = React.useRef<ActiveCollection | null>(null);

  const appendCollectReply = React.useCallback(
    (res: AssistantCollectResponse) => {
      const status = res.status === 'review' || res.status === 'handoff' ? res.status : 'collecting';
      collectRef.current = {
        requestType: res.requestType,
        displayName: res.displayName,
        collected: res.collected ?? {},
        skippedFields: res.skippedFields ?? [],
        reviewFields: res.reviewFields,
      };
      setCollectStatus(status);

      const msg: AssistantMessage = { role: 'assistant', content: res.reply, collectTurn: true };
      if (status === 'review' || status === 'handoff') {
        msg.collect = {
          attachments: res.attachments,
          reviewFields: res.reviewFields,
          status,
          requestType: res.requestType,
          displayName: res.displayName,
          draft: res.draft ?? undefined,
          titlePreview: res.titlePreview ?? undefined,
          formUrl: res.formUrl ?? undefined,
          externalUrl: res.externalUrl ?? undefined,
          collected: res.collected ?? {},
        };
      }
      setMessages((prev) => [...prev, msg]);
    },
    [],
  );

  const replaceCollectReview = React.useCallback((res: AssistantCollectResponse) => {
    setMessages((prev) => {
      const reverseIndex = [...prev].reverse().findIndex(
        message => message.role === 'assistant'
          && message.collectTurn
          && message.collect?.status === 'review',
      );
      if (reverseIndex < 0) return prev;

      const index = prev.length - 1 - reverseIndex;
      return prev.map((message, messageIndex) => messageIndex === index
        ? {
          ...message,
          content: res.reply,
          collect: {
            attachments: res.attachments,
            reviewFields: res.reviewFields,
            status: 'review',
            requestType: res.requestType,
            displayName: res.displayName,
            draft: res.draft ?? undefined,
            titlePreview: res.titlePreview ?? undefined,
            formUrl: res.formUrl ?? undefined,
            externalUrl: res.externalUrl ?? undefined,
            collected: res.collected ?? {},
          },
        }
        : message);
    });
  }, []);

  const startCollect = React.useCallback(
    async (requestType: string, displayName: string) => {
      if (loadingRef.current) return;
      evidenceSubmission.current = newEvidenceSubmission();
      setEvidenceFiles([]); setReviewValues({});
      loadingRef.current = true;
      collectRef.current = { requestType, displayName, collected: {}, skippedFields: [] };
      setCollectStatus('collecting');
      setLoading(true);
      try {
        const res = await sendAssistantCollectTurn({
          requestType,
          collected: {},
          skippedFields: [],
          conversationId: conversationIdRef.current,
        });
        appendCollectReply(res);
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'The guided request could not be started. Please try the form instead.';
        setCollectStatus('idle');
        collectRef.current = null;
        setMessages((prev) => [...prev, { role: 'assistant', content: message, error: true }]);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [appendCollectReply],
  );

  const submitCollectAnswer = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      const active = collectRef.current;
      if (!trimmed || loadingRef.current || !active) return;
      loadingRef.current = true;

      setMessages((prev) => [...prev, { role: 'user', content: trimmed, collectTurn: true }]);
      setLoading(true);
      try {
        const res = await sendAssistantCollectTurn({
          requestType: active.requestType,
          collected: active.collected,
          skippedFields: active.skippedFields,
          message: trimmed,
          conversationId: conversationIdRef.current,
        });
        appendCollectReply(res);
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'The assistant is unavailable right now. Please try again in a moment.';
        setMessages((prev) => [...prev, { role: 'assistant', content: message, error: true }]);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [appendCollectReply],
  );

  const refreshCollectReview = React.useCallback(
    async (values: Record<string, string>) => {
      const active = collectRef.current;
      if (loadingRef.current || !active) return;
      loadingRef.current = true;
      setLoading(true);
      try {
        const { payload, skippedFields } = mergeReviewValues(active, values);
        const normalized = await sendAssistantCollectTurn({
          requestType: active.requestType,
          collected: payload,
          skippedFields,
          conversationId: conversationIdRef.current,
        });
        setReviewValues({});
        if (normalized.status !== 'review') {
          appendCollectReply(normalized);
          return;
        }

        collectRef.current = {
          requestType: normalized.requestType,
          displayName: normalized.displayName,
          collected: normalized.collected,
          skippedFields: normalized.skippedFields,
          reviewFields: normalized.reviewFields,
        };
        replaceCollectReview(normalized);
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'The updated review could not be validated. Please try again.';
        setMessages((prev) => [...prev, { role: 'assistant', content: message, error: true }]);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [appendCollectReply, replaceCollectReview],
  );

  const confirmCollectSubmit = React.useCallback(async (): Promise<string | null> => {
    const active = collectRef.current;
    if (loadingRef.current || !active) return null;
    loadingRef.current = true;
    setLoading(true);
    try {
      const { payload, skippedFields } = mergeReviewValues(active, reviewValues);

      const normalized = await sendAssistantCollectTurn({
        requestType: active.requestType,
        collected: payload,
        skippedFields,
        conversationId: conversationIdRef.current,
      });
      if (normalized.status !== 'review') {
        setReviewValues({});
        appendCollectReply(normalized);
        return null;
      }

      collectRef.current = {
        requestType: normalized.requestType,
        displayName: normalized.displayName,
        collected: normalized.collected,
        skippedFields: normalized.skippedFields,
        reviewFields: normalized.reviewFields,
      };
      setReviewValues({});
      replaceCollectReview(normalized);
      const created = await submitWithEvidence(evidenceSubmission.current,
        () => createRequest(normalized.requestType, normalized.collected), evidenceFiles);
      collectRef.current = null;
      setCollectStatus('idle');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Your ${active.displayName} request has been submitted.`, collectTurn: true },
      ]);
      return created.id;
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'The request could not be submitted. Please try the form instead.';
      setMessages((prev) => [...prev, { role: 'assistant', content: message, error: true }]);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [appendCollectReply, evidenceFiles, replaceCollectReview, reviewValues]);

  const cancelCollect = React.useCallback(() => {
    if (loadingRef.current) return;
    if (!collectRef.current) return;
    collectRef.current = null;
    setCollectStatus('idle');
    const created = evidenceSubmission.current.created;
    setMessages((prev) => [...prev, { role: 'assistant', content: created
      ? `The request is already saved at /requests/${created.id}. Any unconfirmed attachments still need to be uploaded there.`
      : 'No problem — I have cancelled that request.', collectTurn: true }]);
    evidenceSubmission.current = newEvidenceSubmission();
    setEvidenceFiles([]); setReviewValues({});
  }, []);

  const value = React.useMemo<AssistantContextValue>(
    () => ({
      messages,
      loading,
      open,
      collectStatus,
      openAssistant,
      closeAssistant,
      resetConversation,
      sendMessage,
      sendFeedback,
      startCollect,
      submitCollectAnswer,
      confirmCollectSubmit,
      refreshCollectReview,
      cancelCollect,
      evidenceFiles, setEvidenceFiles, reviewValues, setReviewValues,
      savedRequestId: evidenceSubmission.current.created?.id,
    }),
    [
      messages,
      loading,
      open,
      collectStatus,
      openAssistant,
      closeAssistant,
      resetConversation,
      sendMessage,
      sendFeedback,
      startCollect,
      submitCollectAnswer,
      confirmCollectSubmit,
      refreshCollectReview,
      cancelCollect,
      evidenceFiles, reviewValues,
    ],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};

export function useAssistant(): AssistantContextValue {
  const ctx = React.useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within an AssistantProvider');
  return ctx;
}
